import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, AlertTriangle, CheckCircle2, CalendarClock } from "lucide-react";

// Catalog Fulfillment Scheduling Phase 1 (Sep 2026) — catalog-level
// fulfillment hours.
//
// Every availability answer on this screen comes from the backend. The
// preview calls `/schedule/preview`, which calls the SAME service the
// storefront calls, so what an operator sees here is what a customer
// gets. No slot maths exists in this file, deliberately: a second
// implementation in React is exactly how a preview starts lying.
//
// Scope note for anyone extending this: Phase 1 governs Pickup and
// Takeout only. `applicable_modes` comes from the backend rather than
// being hard-coded here, so widening scope later is a backend change.
//
// Gate C6 adds ONE more stored field, `delivery_enabled` — the
// per-catalog opt-in for scheduled Uber Direct delivery. It must be
// round-tripped through this form like every other field, because the
// schedule PUT is a WHOLE REPLACEMENT (deliberately — see `ScheduleIn`)
// and `delivery_enabled` defaults to false server-side. A form that
// merely omits it therefore does not "leave it alone": it silently
// turns scheduled delivery off the next time anyone edits opening
// hours. That was a real defect, and it is what the round-trip below
// and the regression tests beside this file exist to prevent.
//
// Eligibility is NOT decided here. The backend returns
// `scheduled_delivery.eligible` from one conjunctive gate
// (`services/scheduling/delivery_eligibility.py`); this file only
// renders it. A second eligibility opinion in React is precisely how
// the storefront would start offering windows checkout refuses.

const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];

const EMPTY_WEEKLY = Object.fromEntries(DAYS.map(([key]) => [key, []]));

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/** Server-shaped config -> a form state that always has all seven days. */
function toFormState(schedule) {
  const weekly = { ...EMPTY_WEEKLY };
  for (const [key] of DAYS) {
    weekly[key] = Array.isArray(schedule?.weekly?.[key]) ? schedule.weekly[key].map((p) => ({ ...p })) : [];
  }
  return {
    enabled: !!schedule?.enabled,
    weekly,
    lead_time_minutes: Number.isFinite(schedule?.lead_time_minutes) ? schedule.lead_time_minutes : 20,
    max_advance_days: Number.isFinite(schedule?.max_advance_days) ? schedule.max_advance_days : 7,
    date_overrides: Array.isArray(schedule?.date_overrides)
      ? schedule.date_overrides.map((o) => ({ ...o, periods: (o.periods || []).map((p) => ({ ...p })) }))
      : [],
    // Gate C6 — read back verbatim so an ordinary hours edit re-sends
    // the stored value instead of dropping it.
    delivery_enabled: !!schedule?.delivery_enabled,
  };
}

/** Why the backend says this catalog cannot offer scheduled delivery.
 *
 * Display text only — the decision itself is the backend's, and the
 * fallback keeps an unrecognised future reason legible rather than
 * blank. */
const SCHEDULED_DELIVERY_REASONS = {
  delivery_mode_disabled_on_catalog: "Delivery is not enabled on this catalog.",
  scheduled_delivery_not_enabled_for_catalog: "Scheduled delivery is not switched on for this catalog yet.",
  local_delivery_provider_not_uber: "This catalog's delivery provider is not Uber Direct.",
  uber_direct_not_enabled: "Uber Direct is not enabled for your account.",
  uber_direct_quoting_disabled: "Uber Direct quoting is not enabled for your account.",
  scheduling_disabled: "Turn on fulfillment scheduling above first.",
  not_delivery_mode: "This catalog's delivery provider is not Uber Direct.",
};

const inputCls = "border border-stone-300 rounded px-2 py-1 text-sm";
const btnGhost = "text-xs text-stone-600 hover:text-stone-900 inline-flex items-center gap-1";

function PeriodRows({ periods, onChange, testIdPrefix }) {
  return (
    <div className="space-y-1.5">
      {periods.map((period, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            type="time" className={inputCls} value={period.start || ""}
            data-testid={`${testIdPrefix}-start-${index}`}
            onChange={(e) => onChange(periods.map((p, i) => (i === index ? { ...p, start: e.target.value } : p)))}
          />
          <span className="text-stone-400 text-sm">to</span>
          <input
            type="time" className={inputCls} value={period.end || ""}
            data-testid={`${testIdPrefix}-end-${index}`}
            onChange={(e) => onChange(periods.map((p, i) => (i === index ? { ...p, end: e.target.value } : p)))}
          />
          <button
            type="button" className="text-stone-400 hover:text-red-600"
            data-testid={`${testIdPrefix}-remove-${index}`}
            onClick={() => onChange(periods.filter((_, i) => i !== index))}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button" className={btnGhost} data-testid={`${testIdPrefix}-add`}
        onClick={() => onChange([...periods, { start: "11:00", end: "14:00" }])}
      >
        <Plus className="w-3 h-3" /> Add hours
      </button>
    </div>
  );
}

function AvailabilityPreview({ catalogId, reloadKey }) {
  const [now, setNow] = useState(null);
  const [date, setDate] = useState(todayIso());
  const [dayView, setDayView] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/retail/catalogs/${catalogId}/schedule/preview`)
      .then(({ data }) => { if (alive) setNow(data); })
      .catch(() => { if (alive) setNow(null); });
    return () => { alive = false; };
  }, [catalogId, reloadKey]);

  const loadDate = useCallback(async (value) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/retail/catalogs/${catalogId}/schedule/preview/date`, {
        params: { date: value },
      });
      setDayView(data);
    } catch (e) {
      setDayView(null);
      toast.error(e?.response?.data?.detail?.message || "Could not load that date");
    } finally {
      setBusy(false);
    }
  }, [catalogId]);

  useEffect(() => { loadDate(date); }, [loadDate, date, reloadKey]);

  if (!now) return null;

  return (
    <div className="mt-6 border-t border-stone-200 pt-4" data-testid="schedule-preview">
      <h4 className="font-medium text-sm flex items-center gap-1.5">
        <CalendarClock className="w-4 h-4" /> What customers see
      </h4>
      <p className="text-xs text-stone-500 mt-0.5">
        Calculated by the same service the storefront uses — not an estimate.
      </p>

      <div className="mt-3 rounded border border-stone-200 p-3 bg-stone-50 text-sm" data-testid="schedule-preview-now">
        {!now.scheduling_enabled ? (
          <p className="text-stone-600">
            Scheduling is off for this catalog. Customers order exactly as they do today.
          </p>
        ) : now.asap_available ? (
          <p className="text-emerald-800" data-testid="schedule-preview-asap-yes">
            <strong>ASAP available</strong> — {now.asap_window?.label}
            <span className="text-stone-500"> (lead time {now.lead_time_minutes} min)</span>
          </p>
        ) : (
          <div className="text-amber-800" data-testid="schedule-preview-asap-no">
            <p><strong>Not available for immediate fulfillment right now.</strong></p>
            {now.earliest_available ? (
              <p className="mt-0.5">
                Earliest: {now.earliest_available.label} on {now.earliest_available.date || now.earliest_available.local_date}
              </p>
            ) : (
              <p className="mt-0.5">No availability within the next {now.max_advance_days} days.</p>
            )}
            <p className="text-xs text-stone-500 mt-0.5">Lead time {now.lead_time_minutes} min · {now.timezone}</p>
          </div>
        )}
      </div>

      {now.scheduling_enabled && (
        <div className="mt-3">
          <label className="text-xs text-stone-600">Preview another date</label>
          <input
            type="date" className={`${inputCls} ml-2`} value={date}
            data-testid="schedule-preview-date"
            onChange={(e) => setDate(e.target.value)}
          />
          {busy && <Loader2 className="w-4 h-4 animate-spin inline ml-2 text-stone-400" />}
          <div className="mt-2 text-sm" data-testid="schedule-preview-day">
            {dayView && dayView.closed ? (
              <p className="text-stone-600">
                Closed{dayView.special_date ? " — special date" : ""}
                {dayView.note ? ` (${dayView.note})` : ""}
              </p>
            ) : dayView ? (
              <>
                <p className="text-stone-600 mb-1">
                  {dayView.windows.length} window{dayView.windows.length === 1 ? "" : "s"}
                  {dayView.special_date ? " — special date" : ""}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {dayView.windows.map((w) => (
                    <span key={w.start} className="text-xs bg-white border border-stone-300 rounded px-1.5 py-0.5">
                      {w.label}
                    </span>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CatalogSchedulingPanel({ catalog }) {
  const catalogId = catalog?.id;
  const [loaded, setLoaded] = useState(false);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(() => toFormState(null));
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    if (!catalogId) return;
    try {
      const { data } = await api.get(`/retail/catalogs/${catalogId}/schedule`);
      setMeta(data);
      setForm(toFormState(data.schedule));
    } catch (_e) {
      setMeta(null);
    } finally {
      setLoaded(true);
    }
  }, [catalogId]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setDay = (day, periods) =>
    setForm((prev) => ({ ...prev, weekly: { ...prev.weekly, [day]: periods } }));

  const canEnable = !!meta?.can_enable;
  const maxAdvance = meta?.platform?.max_advance_days ?? 30;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/retail/catalogs/${catalogId}/schedule`, form);
      setMeta(data);
      setForm(toFormState(data.schedule));
      setReloadKey((k) => k + 1);
      toast.success(`Schedule saved (version ${data.published_version})`);
    } catch (e) {
      // The backend returns {code, message} for every refusal; show the
      // operator the message rather than a generic failure, because every
      // one of them names something they can fix.
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || "Could not save the schedule");
    } finally {
      setSaving(false);
    }
  };

  const applicable = useMemo(() => meta?.applicable_modes || [], [meta]);
  // Gate C6 — present only on a backend that reports it, so an older
  // response simply renders no scheduled-delivery control at all and
  // this panel behaves exactly as it did before C6.
  const scheduledDelivery = meta?.scheduled_delivery || null;
  const scheduledDeliveryEligible = !!scheduledDelivery?.eligible;
  // Whether the operator may switch the opt-in ON — NOT whether scheduled
  // delivery is eligible today. Those differ for exactly the catalog that
  // matters here: one that has never opted in. Gating the control on
  // `eligible` made the first opt-in impossible, because eligibility
  // refuses on the absence of the very value this control sets.
  //
  // `can_opt_in` is the backend's own answer, from the same evaluator, so
  // no eligibility rule is restated in the browser. A backend that does
  // not report it (an older deployment) falls back to `eligible`, which
  // is exactly the previous behaviour.
  const canOptIn = scheduledDelivery
    ? ("can_opt_in" in scheduledDelivery
        ? !!scheduledDelivery.can_opt_in
        : scheduledDeliveryEligible)
    : false;
  // Scheduling itself is saved in the SAME request, so this follows the
  // live form rather than the stored document — an operator switching
  // scheduling on gets the delivery control immediately, with no round
  // trip, and switching it off withdraws it just as directly.
  const deliveryOptInAllowed = canOptIn && !!form?.enabled;
  const optInBlockedReason = scheduledDelivery?.opt_in_blocked_reason
    ?? scheduledDelivery?.ineligible_reason;

  if (!loaded || !meta) return null;

  return (
    <div className="border border-stone-200 rounded-lg p-5 mt-6" data-testid="catalog-scheduling-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium">Fulfillment Scheduling</h3>
          <p className="text-sm text-stone-500 mt-0.5">
            When this catalog can fulfill orders. Applies to{" "}
            {applicable.length ? applicable.join(" and ") : "Pickup and Takeout"} orders only —
            Dine-In, Delivery and Shipping are unaffected.
          </p>
        </div>
        {meta.version != null && (
          <span className="text-xs text-stone-400 shrink-0" data-testid="schedule-version">
            v{meta.version}
          </span>
        )}
      </div>

      {!canEnable && (
        <div
          className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2"
          data-testid="schedule-timezone-blocker"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Set your business time zone first.</p>
            <p className="mt-0.5">
              Scheduling is assuming {meta.timezone?.timezone}, which is a platform default rather
              than your configured time zone. Opening hours calculated from it would be wrong.
              Set it in Business Profile, then enable scheduling here.
            </p>
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 mt-4 text-sm">
        <input
          type="checkbox" checked={form.enabled} disabled={!canEnable}
          data-testid="schedule-enabled"
          onChange={(e) => set("enabled", e.target.checked)}
        />
        <span>Enable fulfillment scheduling for this catalog</span>
      </label>
      {!form.enabled && (
        <p className="text-xs text-stone-500 mt-1">
          While this is off, ordering behaves exactly as it does today — no hours are enforced.
        </p>
      )}

      {scheduledDelivery && (
        <div className="mt-4 rounded border border-stone-200 p-3" data-testid="schedule-delivery-section">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.delivery_enabled}
              // Interactive ONLY while the backend says this catalog is
              // eligible. When it is not, the box still SHOWS the stored
              // value — it is never quietly flipped — but it cannot be
              // used to establish a configuration the backend would
              // refuse.
              disabled={!deliveryOptInAllowed}
              data-testid="schedule-delivery-enabled"
              onChange={(e) => {
                if (!deliveryOptInAllowed) return;
                set("delivery_enabled", e.target.checked);
              }}
            />
            <span>
              Offer scheduled Uber Direct delivery
              <span className="block text-xs text-stone-500 mt-0.5">
                Customers can choose a delivery window today, up to{" "}
                {scheduledDelivery.max_horizon_hours ?? 8} hours ahead. Orders close{" "}
                {scheduledDelivery.ordering_cutoff_minutes ?? 60} minutes before the window.
                Pickup and Takeout scheduling is unaffected.
              </span>
            </span>
          </label>

          {/* Explain a REAL blocker only. When the sole thing standing in
              the way is this switch, printing "scheduled delivery is not
              switched on" beside the switch that turns it on is circular
              and tells the operator nothing they can act on. */}
          {!deliveryOptInAllowed && (
            <p
              className="text-xs text-amber-800 mt-2"
              data-testid="schedule-delivery-ineligible"
            >
              {SCHEDULED_DELIVERY_REASONS[optInBlockedReason]
                || "Scheduled delivery is not available for this catalog yet."}
              {form.delivery_enabled
                ? " This setting is saved and kept as-is, but no delivery windows are offered until the above is resolved."
                : ""}
            </p>
          )}
        </div>
      )}

      <div className="mt-5">
        <h4 className="font-medium text-sm">Weekly hours</h4>
        <div className="mt-2 space-y-3">
          {DAYS.map(([key, label]) => (
            <div key={key} className="grid grid-cols-[7rem,1fr] gap-3 items-start">
              <div className="text-sm pt-1">
                {label}
                {form.weekly[key].length === 0 && (
                  <span className="block text-xs text-stone-400">Closed</span>
                )}
              </div>
              <PeriodRows
                periods={form.weekly[key]}
                onChange={(periods) => setDay(key, periods)}
                testIdPrefix={`schedule-${key}`}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-stone-500 mt-2">
          A period cannot run past midnight. To trade past midnight, use two periods — for example
          18:00–24:00 on Friday and 00:00–01:00 on Saturday.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <div>
          <label className="text-sm">Minimum lead time (minutes)</label>
          <input
            type="number" min={0} max={meta.platform?.max_lead_time_minutes ?? 1440}
            className={`${inputCls} mt-1 block w-32`} value={form.lead_time_minutes}
            data-testid="schedule-lead-time"
            onChange={(e) => set("lead_time_minutes", Number(e.target.value))}
          />
          <p className="text-xs text-stone-500 mt-1">How long you need before an order is ready.</p>
        </div>
        <div>
          <label className="text-sm">Order up to (days ahead)</label>
          <input
            type="number" min={0} max={maxAdvance}
            className={`${inputCls} mt-1 block w-32`} value={form.max_advance_days}
            data-testid="schedule-max-advance"
            onChange={(e) => set("max_advance_days", Number(e.target.value))}
          />
          {/* This field governs Pickup and Takeout only. Scheduled Uber
              Direct delivery is same-day with its own 8-hour ceiling
              (see the delivery section above), so an operator reading
              "7 days" here would otherwise reasonably expect customers
              to be offered delivery a week out. */}
          <p className="text-xs text-stone-500 mt-1">
            Applies to Pickup and Takeout. Platform maximum is {maxAdvance} days.
          </p>
          {scheduledDelivery?.available && form.delivery_enabled && (
            <p className="text-xs text-stone-500 mt-1" data-testid="schedule-max-advance-delivery-note">
              Scheduled Uber Direct delivery is not affected by this setting — it stays
              same-day, up to {scheduledDelivery.max_horizon_hours ?? 8} hours ahead.
            </p>
          )}
        </div>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-sm">Special dates</h4>
        <p className="text-xs text-stone-500 mt-0.5">
          Holidays and one-off hours. A special date replaces that day's normal hours entirely.
        </p>
        <div className="mt-2 space-y-3">
          {form.date_overrides.map((override, index) => (
            <div key={index} className="border border-stone-200 rounded p-3">
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="date" className={inputCls} value={override.date || ""}
                  data-testid={`schedule-override-date-${index}`}
                  onChange={(e) => set("date_overrides", form.date_overrides.map(
                    (o, i) => (i === index ? { ...o, date: e.target.value } : o),
                  ))}
                />
                <label className="text-sm flex items-center gap-1.5">
                  <input
                    type="checkbox" checked={!!override.closed}
                    data-testid={`schedule-override-closed-${index}`}
                    onChange={(e) => set("date_overrides", form.date_overrides.map(
                      (o, i) => (i === index
                        ? { ...o, closed: e.target.checked, periods: e.target.checked ? [] : o.periods }
                        : o),
                    ))}
                  />
                  Closed all day
                </label>
                <input
                  type="text" className={`${inputCls} flex-1 min-w-[8rem]`} placeholder="Note (optional)"
                  value={override.note || ""} maxLength={200}
                  data-testid={`schedule-override-note-${index}`}
                  onChange={(e) => set("date_overrides", form.date_overrides.map(
                    (o, i) => (i === index ? { ...o, note: e.target.value } : o),
                  ))}
                />
                <button
                  type="button" className="text-stone-400 hover:text-red-600"
                  data-testid={`schedule-override-remove-${index}`}
                  onClick={() => set("date_overrides", form.date_overrides.filter((_, i) => i !== index))}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {!override.closed && (
                <div className="mt-2 pl-1">
                  <PeriodRows
                    periods={override.periods || []}
                    onChange={(periods) => set("date_overrides", form.date_overrides.map(
                      (o, i) => (i === index ? { ...o, periods } : o),
                    ))}
                    testIdPrefix={`schedule-override-${index}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          type="button" className={`${btnGhost} mt-2`} data-testid="schedule-override-add"
          onClick={() => set("date_overrides", [
            ...form.date_overrides, { date: todayIso(), closed: true, periods: [], note: "" },
          ])}
        >
          <Plus className="w-3 h-3" /> Add special date
        </button>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          className="bg-stone-900 text-white text-sm rounded px-3 py-1.5 disabled:opacity-50"
          onClick={save} disabled={saving} data-testid="schedule-save"
        >
          {saving ? "Saving…" : "Save schedule"}
        </button>
        {meta.configured && (
          <span className="text-xs text-stone-500 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Saved
          </span>
        )}
      </div>

      {catalogId && <AvailabilityPreview catalogId={catalogId} reloadKey={reloadKey} />}
    </div>
  );
}
